import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../contexts/ThemeContext';
import { Visibility, VisibilityOff, ArrowBack, CheckCircle, Warning } from '@mui/icons-material';
import { resetPassword } from '../services/authService';
import { API_CONFIG, APP_NAME } from '../helpers/constants';
import { decodeResetToken, encryptPassword } from '../utils/crypto';
import {
  validatePasswordRequirements,
  isPasswordValid,
  type PasswordValidation,
} from '../helpers/passwordUtils';
import PasswordPolicyIndicator from '../components/PasswordPolicyIndicator';
import PasswordMatchIndicator from '../components/PasswordMatchIndicator';

const ResetPassword: React.FC = () => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');
  const [tokenValid, setTokenValid] = useState(true);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [tokenUsed, setTokenUsed] = useState(false);
  const [fieldsFromToken, setFieldsFromToken] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    minLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Utility function to manage used tokens with timestamps
  const markTokenAsUsed = (token: string) => {
    const usedTokens = JSON.parse(localStorage.getItem('usedResetTokens') || '[]');
    
    const tokenWithTimestamp = {
      token,
      usedAt: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours from now
    };
    usedTokens.push(tokenWithTimestamp);
    
    // Clean up expired tokens (older than 24 hours)
    const validTokens = usedTokens.filter((t: any) => 
      typeof t === 'string' || (t.expiresAt && Date.now() < t.expiresAt)
    );
    
    localStorage.setItem('usedResetTokens', JSON.stringify(validTokens));
  };

  const isTokenUsed = (token: string): boolean => {
    const usedTokens = JSON.parse(localStorage.getItem('usedResetTokens') || '[]');
    
    return usedTokens.some((t: any) => 
      (typeof t === 'string' && t === token) || 
      (t.token && t.token === token)
    );
  };


  // Extract and decode token from URL parameters
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
      
      // Check if this token has already been used
      if (isTokenUsed(tokenParam)) {
        setTokenUsed(true);
        setTokenValid(false);
        return;
      }
      
      // Decode the token to extract email, user_id, and expiry
      const decodeToken = async () => {
        try {
          const decodedData = await decodeResetToken(tokenParam);
          
          if (decodedData.valid) {
            // Additional validation: Check if token is expired based on creation time
            const currentTime = Date.now();
            const tokenCreationTime = decodedData.expiry;
            const oneHourInMs = 60 * 60 * 1000; // 1 hour in milliseconds
            
            if (tokenCreationTime && (currentTime - tokenCreationTime) > oneHourInMs) {
              setTokenExpired(true);
              setTokenValid(false);
              return;
            }
            
            setEmail(decodedData.email);
            setUserId(decodedData.user_id);
            setFieldsFromToken(true);
            setTokenValid(true);
            setTokenExpired(false);
          } else {
            setTokenValid(false);
            // Check if token is expired
            if (decodedData.expiry && Date.now() > decodedData.expiry) {
              setTokenExpired(true);
            }
          }
        } catch (error) {
          console.error('Token decoding error:', error);
          setTokenValid(false);
        }
      };
      
      decodeToken();
    } else {
      // Fallback to URL parameters if no token
      const emailParam = searchParams.get('email');
      const userIdParam = searchParams.get('user_id');
      
      if (emailParam) setEmail(emailParam);
      if (userIdParam) setUserId(userIdParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Check if token has already been used
      if (isTokenUsed(token)) {
        setTokenUsed(true);
        setError('This reset link has already been used. Please request a new password reset.');
        setLoading(false);
        return;
      }

      // Check if token is valid and not expired
      if (!tokenValid) {
        setError('Invalid or expired reset link. Please request a new password reset.');
        setLoading(false);
        return;
      }

      if (tokenExpired) {
        setError('This reset link has expired. Please request a new password reset.');
        setLoading(false);
        return;
      }

      // Additional token validation - re-check expiration
      if (token) {
        try {
          const decodedData = await decodeResetToken(token);
          const currentTime = Date.now();
          const tokenCreationTime = decodedData.expiry;
          const oneHourInMs = 60 * 60 * 1000; // 1 hour in milliseconds
          
          if (tokenCreationTime && (currentTime - tokenCreationTime) > oneHourInMs) {
            setTokenExpired(true);
            setError('This reset link has expired. Please request a new password reset.');
            setLoading(false);
            return;
          }
        } catch (error) {
          setError('Invalid reset link. Please request a new password reset.');
          setLoading(false);
          return;
        }
      }

      if (!email || !newPassword || !confirmPassword) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      if (!userId) {
        setError('Invalid reset link. User ID not found.');
        setLoading(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      if (!isPasswordValid(passwordValidation)) {
        setError('Password must meet all requirements');
        setLoading(false);
        return;
      }

      // Encrypt the passwords using AES-GCM encryption
      const encryptedNewPassword = await encryptPassword(newPassword);
      // const encryptedConfirmPassword = await encryptPassword(confirmPassword);

      // Call the reset password API
      const response = await resetPassword({
        base_url: API_CONFIG.NODE_BASE_URL,
        email_id: email,
        user_id: userId,
        new_password: encryptedNewPassword,
        confirm_new_password: encryptedNewPassword,
        encrypted: true
      });
      
      // Check for both 'status: success' and 'success: true' formats
      if (response.status === 'success' || (response as any).success === true) {
        // Mark this token as used to prevent reuse
        markTokenAsUsed(token);
        
        setSuccess(true);
        
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(response.message || 'Failed to reset password');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show token used error screen
  if (tokenUsed) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Warning sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Link Already Used
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This password reset link has already been used
            </Typography>
          </Box>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            Password reset links can only be used once for security reasons.
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              onClick={() => navigate('/forgot-password')}
              fullWidth
              variant="contained"
              sx={{ mt: 1 }}
            >
              Request New Reset Link
            </Button>
            
            <Button
              onClick={() => navigate('/login')}
              variant="outlined"
              fullWidth
            >
              Back to Login
            </Button>
          </Box>
        </Paper>

        {/* Copyright below form (Accsell-style footer) */}
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 3 }}
        >
          © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
        </Typography>
      </Box>
    );
  }

  // Show token expired error screen
  if (tokenExpired || !tokenValid) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Warning sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {tokenExpired ? 'Link Expired' : 'Invalid Link'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tokenExpired 
                ? 'This password reset link has expired' 
                : 'This password reset link is invalid or corrupted'
              }
            </Typography>
          </Box>
          
          <Alert severity="error" sx={{ mb: 3 }}>
            {tokenExpired 
              ? 'Password reset links expire after 1 hour for security reasons. Please request a new reset link.'
              : 'The reset link appears to be corrupted or invalid. Please request a new reset link.'
            }
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              onClick={() => navigate('/forgot-password')}
              fullWidth
              variant="contained"
              sx={{ mt: 1 }}
            >
              Request New Reset Link
            </Button>
            
            <Button
              onClick={() => navigate('/login')}
              variant="outlined"
              fullWidth
            >
              Back to Login
            </Button>
          </Box>
        </Paper>

        {/* Copyright below form (Accsell-style footer) */}
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 3 }}
        >
          © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      {/* Logo above the card */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box
          component="img"
          src={isDarkMode ? '/fulllogo-white.png' : '/fulllogo.png'}
          alt={APP_NAME}
          sx={{
            height: 48,
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
          Reset Password
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Enter your new password below
        </Typography>

        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 2 }}
            icon={<CheckCircle />}
          >
            Password reset successful! Redirecting to login...
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            disabled={loading || fieldsFromToken}
            placeholder="Enter your email address"
            sx={fieldsFromToken ? { backgroundColor: 'grey.50' } : {}}
          />
          {fieldsFromToken && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Email is pre-filled from your reset link
            </Typography>
          )}

          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
              const value = e.target.value;
              setNewPassword(value);
              setPasswordValidation(validatePasswordRequirements(value));
              if (confirmPassword) {
                setPasswordsMatch(value === confirmPassword);
              }
            }}
            margin="normal"
            required
            disabled={loading}
            placeholder="Enter new password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <PasswordPolicyIndicator
            validation={passwordValidation}
            show={!!newPassword}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              const value = e.target.value;
              setConfirmPassword(value);
              setPasswordsMatch(newPassword === value);
            }}
            margin="normal"
            required
            disabled={loading}
            placeholder="Confirm new password"
            error={confirmPassword.length > 0 && !passwordsMatch}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <PasswordMatchIndicator match={passwordsMatch} show={!!confirmPassword} />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={
              loading ||
              !isPasswordValid(passwordValidation) ||
              !passwordsMatch ||
              !confirmPassword
            }
          >
            {loading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          <Link 
            to="/login" 
            style={{ 
              color: muiTheme.palette.primary.main, 
              textDecoration: 'none',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
            Back to Login
          </Link>
        </Typography>
      </Paper>

      {/* Copyright below form (Accsell-style footer) */}
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ mt: 3 }}
      >
        © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
      </Typography>
    </Box>
  );
};

export default ResetPassword;
